import React, { useEffect, useState } from "react";
import { IonContent, IonPage, useIonToast, IonCard, IonCardContent, IonButton, IonIcon } from "@ionic/react";
import { alertCircleOutline, createOutline, documentTextOutline, refreshOutline } from "ionicons/icons";

import { Salario } from "src/utils/interfaces";

import SalariosAPI from "src/services/salarios";
import { getEstadoPerfil } from "src/services/operarioPerfil";
import ClimaOrganizacionalAPI from "src/services/climaOrganizacional";
import { obtenerFeatureSwitches } from "src/services/featureSwitches";
import { obtenerEstadoFirmaRecibo, FirmaReciboEstado } from "src/services/firmaRecibo";

import Header from "src/components/Header";
import Recibo from "src/components/Recibos/Recibo";

import { capitalize } from "src/utils/especificos";
import { useHistory } from "react-router";

const RecibosSalariales: React.FC = () => {
  const [salarios, setSalarios] = useState<Salario[]>([]);
  const [present] = useIonToast();
  // estado para verificar si el perfil está completo
  const [perfilCompleto, setPerfilCompleto] = useState<boolean | null>(null);
  // estado para verificar si el clima organizacional ya fue completado
  const [climaCompleto, setClimaCompleto] = useState<boolean | null>(null);
  // estado de la firma del recibo en RRHH (null = todavía no se verificó)
  const [firmaRecibo, setFirmaRecibo] = useState<FirmaReciboEstado | null>(null);
  // estado para controlar la carga de la verificación
  const [cargandoAcceso, setCargandoAcceso] = useState(true);
  const history = useHistory();

  useEffect(() => {
    // verificar el acceso antes de cargar salarios
    verificarAcceso();
  }, []);

  // validar perfil, clima y firma del recibo en RRHH antes de mostrar los recibos
  const verificarAcceso = async () => {
    try {
      // consultar el estado del perfil al backend
      const estado = await getEstadoPerfil();
      // si needsUpdate es false, el perfil está completo
      setPerfilCompleto(!estado.needsUpdate);
      
      // si no completó perfil, detenemos aquí
      if (estado.needsUpdate) {
        setClimaCompleto(null);
        return;
      }

      // si clima no está habilitado por switch, no bloqueamos salarios por clima
      const switches = await obtenerFeatureSwitches();
      const climaHabilitado = Boolean(switches?.climaOrganizacional);

      if (!climaHabilitado) {
        setClimaCompleto(true);
        await verificarFirmaRecibo();
        return;
      }

      const estadoClimaResp = await ClimaOrganizacionalAPI.obtenerEstado();
      const yaRealizadaClima = Boolean(estadoClimaResp?.data?.ya_realizada);
      setClimaCompleto(yaRealizadaClima);

      // solo verificar firma y cargar salarios si clima fue completado
      if (yaRealizadaClima) {
        await verificarFirmaRecibo();
      }
    } catch (error) {
      // si falla la verificación, bloqueamos el acceso por seguridad
      setPerfilCompleto(false);
      setClimaCompleto(false);
      present({
        message: "No se pudo verificar el acceso al módulo de Recibos Salariales",
        duration: 3000,
        color: "warning"
      });
    } finally {
      // finalizar estado de carga
      setCargandoAcceso(false);
    }
  };

  // validar que RRHH ya haya registrado la firma del recibo del periodo vigente.
  // Si hay periodo de firma abierto y todavía no firmó, no se cargan los recibos:
  // se muestra el cartel para pasar por RRHH.
  const verificarFirmaRecibo = async () => {
    let estadoFirma: FirmaReciboEstado = {
      bloqueado: false,
      firmado: true,
      periodo: null,
      fecha_firma: null,
      mensaje: "",
    };

    try {
      estadoFirma = await obtenerEstadoFirmaRecibo();
    } catch (error) {
      // si falla el control de firma no dejamos al colaborador sin sus recibos:
      // el backend igual valida el bloqueo al pedir los recibos.
    }

    setFirmaRecibo(estadoFirma);

    if (!estadoFirma.bloqueado) {
      cargarSalarios();
    }
  };

  // cargar los recibos salariales desde el backend
  const cargarSalarios = () => {
    SalariosAPI.getSalarios()
      .then((response) => {
        const salariosSinFormatear = response.data.recibos_salariales;
        const salariosFormateados = salariosSinFormatear.map(
          (salarioRaiz: any) => {
            const mes = Object.keys(salarioRaiz)[0];
            const salario = salarioRaiz[mes];

            const salariosDelMes: Salario = {
              mes: mes,
              bruto: salario.cabecera["BRUTO A COBRAR"],
              neto: salario.cabecera["NETO A COBRAR"],
              detalles: [],
              horas: [],
            };

            if (salario.detalle["SALARIO BASE"]) {
              const base = {
                descripcion: capitalize("SALARIO BASE"),
                valor: salario.detalle["SALARIO BASE"],
              };
              salariosDelMes.detalles.push({
                descripcion: base.descripcion,
                valor: base.valor,
              });
              delete salario.detalle["SALARIO BASE"];
            }

            const ips = {
              descripcion: "I.P.S.",
              valor: salario.detalle["I.P.S."],
            };
            delete salario.detalle["I.P.S."];

            for (const detalle in salario.detalle) {
              const valor = salario.detalle[detalle];

              salariosDelMes.detalles.push({
                descripcion: capitalize(detalle),
                valor: valor,
              });
            }

            if (ips.valor) {
              salariosDelMes.detalles.push({
                descripcion: ips.descripcion,
                valor: ips.valor,
              });
            }

            if (salario.horas_trabajadas) {
              for (const descripcion in salario.horas_trabajadas) {
                const valor = salario.horas_trabajadas[descripcion];

                salariosDelMes.horas?.push({
                  descripcion: capitalize(descripcion),
                  valor: valor,
                });
              }
            }

            return salariosDelMes;
          }
        );

        setSalarios(salariosFormateados);
      })
      .catch((error) => {
        // el backend también valida la firma del recibo: si bloqueó, mostramos
        // el cartel de RRHH en lugar del error genérico
        const respuesta = error?.response?.data;
        if (error?.response?.status === 403 && respuesta?.bloqueado_firma_recibo) {
          setFirmaRecibo({
            bloqueado: true,
            firmado: false,
            periodo: respuesta?.periodo ?? null,
            fecha_firma: null,
            mensaje: respuesta?.mensaje ?? "",
          });
          return;
        }

        present({
          message: "Ocurrió un error al cargar salarios",
          duration: 5000,
        });
      });
  };

  // redirigir al home para que el usuario actualice sus datos
  const irAActualizar = () => {
    history.push("/home");
  };

  // volver a consultar el acceso (útil después de firmar el recibo en RRHH)
  const reintentarVerificacion = () => {
    setCargandoAcceso(true);
    setFirmaRecibo(null);
    verificarAcceso();
  };

  // pasó perfil y clima, pero RRHH todavía no registró la firma del recibo
  const firmaPendiente =
    !cargandoAcceso &&
    perfilCompleto === true &&
    climaCompleto === true &&
    firmaRecibo?.bloqueado === true;

  // superó las tres validaciones: perfil, clima y firma del recibo en RRHH
  const accesoHabilitado =
    !cargandoAcceso &&
    perfilCompleto === true &&
    climaCompleto === true &&
    firmaRecibo !== null &&
    !firmaRecibo.bloqueado;

  return (
    <IonPage>
      <Header titulo="Recibos Salariales" />

      <IonContent id="salarios">
        <div className="container mb-4">
          {/* mostrar mensaje de carga mientras verifica acceso */}
          {cargandoAcceso && (
            <IonCard>
              <IonCardContent>
                <p className="ion-text-center">Verificando acceso...</p>
              </IonCardContent>
            </IonCard>
          )}

          {/* mostrar mensaje de bloqueo si el perfil no está completo */}
          {!cargandoAcceso && perfilCompleto === false && (
            <IonCard color="light-warning" style={{ maxWidth: '600px', margin: '0 auto' }}>
              <IonCardContent>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px, 3vw, 12px)', marginBottom: 'clamp(12px, 4vw, 16px)' }}>
                  <IonIcon icon={alertCircleOutline} style={{ fontSize: 'clamp(32px, 8vw, 38px)', flexShrink: 0 }} />
                  <h2 style={{ margin: 0, fontSize: 'clamp(18px, 5vw, 22px)', fontWeight: 'bold' }}>Acceso Restringido</h2>
                </div>
                <p style={{ fontSize: 'clamp(15px, 4vw, 17px)', lineHeight: '1.6', marginBottom: 'clamp(12px, 3vw, 14px)' }}>
                  Para acceder a tus recibos salariales, primero debes completar tu información personal en el módulo de actualización de datos.
                </p>
                <div style={{ background: 'rgba(0,0,0,0.1)', padding: 'clamp(10px, 3vw, 12px)', borderRadius: '8px', marginBottom: 'clamp(12px, 3vw, 14px)' }}>
                  <p style={{ fontSize: 'clamp(15px, 4vw, 17px)', lineHeight: '1.6', margin: 0 }}>
                    <strong>⚠️ Importante:</strong> Debes completar tus datos <strong>desde tu hogar</strong> para que la ubicación GPS registrada sea la de tu domicilio real.
                  </p>
                </div>
                <p style={{ fontSize: 'clamp(15px, 4vw, 17px)', lineHeight: '1.6', marginBottom: 'clamp(10px, 3vw, 14px)', fontWeight: 'bold' }}>
                  ¿Por qué es necesario?
                </p>
                <ul style={{ fontSize: 'clamp(14px, 3.8vw, 16px)', lineHeight: '1.6', marginBottom: 'clamp(14px, 4vw, 16px)', paddingLeft: 'clamp(16px, 5vw, 20px)' }}>
                  <li style={{ marginBottom: 'clamp(6px, 2vw, 8px)' }}>
                    Tu <strong>ubicación de domicilio</strong> es fundamental para optimizar las asignaciones de trabajo, priorizando puntos de servicio cercanos a tu hogar.
                  </li>
                  <li style={{ marginBottom: 'clamp(6px, 2vw, 8px)' }}>
                    Los datos de <strong>contacto de emergencia</strong> nos permiten comunicarnos con un familiar en caso de cualquier eventualidad.
                  </li>
                  <li style={{ marginBottom: 'clamp(6px, 2vw, 8px)' }}>
                    Esta información es <strong>requisito obligatorio</strong> para acceder a módulos con datos sensibles como recibos de salario, historial laboral, entre otros.
                  </li>
                </ul>
                <IonButton 
                  expand="block" 
                  color="primary" 
                  onClick={irAActualizar}
                  style={{ marginTop: 'clamp(6px, 2vw, 8px)', fontSize: 'clamp(13px, 3.5vw, 15px)' }}
                >
                  <IonIcon slot="start" icon={createOutline} />
                  IR AL INICIO A ACTUALIZAR MIS DATOS
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {/* mostrar bloqueo por clima organizacional no completado */}
          {!cargandoAcceso && perfilCompleto === true && climaCompleto === false && (
            <IonCard color="light-warning" style={{ maxWidth: '600px', margin: '0 auto' }}>
              <IonCardContent>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px, 3vw, 12px)', marginBottom: 'clamp(12px, 4vw, 16px)' }}>
                  <IonIcon icon={alertCircleOutline} style={{ fontSize: 'clamp(32px, 8vw, 38px)', flexShrink: 0 }} />
                  <h2 style={{ margin: 0, fontSize: 'clamp(18px, 5vw, 22px)', fontWeight: 'bold' }}>Debes completar Clima Organizacional</h2>
                </div>
                <p style={{ fontSize: 'clamp(15px, 4vw, 17px)', lineHeight: '1.6', marginBottom: 'clamp(12px, 3vw, 14px)' }}>
                  Para poder visualizar el módulo de Recibos Salariales, primero debes completar la evaluación de Clima Organizacional.
                </p>
                <IonButton
                  expand="block"
                  color="primary"
                  onClick={() => history.push('/clima-organizacional')}
                  style={{ marginTop: 'clamp(6px, 2vw, 8px)', fontSize: 'clamp(13px, 3.5vw, 15px)' }}
                >
                  IR A COMPLETAR CLIMA ORGANIZACIONAL
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {/* mostrar bloqueo si RRHH todavía no registró la firma del recibo */}
          {firmaPendiente && (
            <IonCard color="light-warning" style={{ maxWidth: '600px', margin: '0 auto' }}>
              <IonCardContent>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px, 3vw, 12px)', marginBottom: 'clamp(12px, 4vw, 16px)' }}>
                  <IonIcon icon={documentTextOutline} style={{ fontSize: 'clamp(32px, 8vw, 38px)', flexShrink: 0 }} />
                  <h2 style={{ margin: 0, fontSize: 'clamp(18px, 5vw, 22px)', fontWeight: 'bold' }}>Debés firmar tu recibo en RRHH</h2>
                </div>
                <p style={{ fontSize: 'clamp(15px, 4vw, 17px)', lineHeight: '1.6', marginBottom: 'clamp(12px, 3vw, 14px)' }}>
                  {firmaRecibo?.mensaje ||
                    "Para volver a ver tus recibos salariales debés pasar por RRHH a firmar tu recibo de salario. Una vez que RRHH registre tu firma, el módulo se habilita automáticamente."}
                </p>
                {firmaRecibo?.periodo?.etiqueta && (
                  <div style={{ background: 'rgba(0,0,0,0.1)', padding: 'clamp(10px, 3vw, 12px)', borderRadius: '8px', marginBottom: 'clamp(12px, 3vw, 14px)' }}>
                    <p style={{ fontSize: 'clamp(15px, 4vw, 17px)', lineHeight: '1.6', margin: 0 }}>
                      <strong>Periodo pendiente de firma:</strong> {firmaRecibo.periodo.etiqueta}
                    </p>
                  </div>
                )}
                <IonButton
                  expand="block"
                  color="primary"
                  onClick={reintentarVerificacion}
                  style={{ marginTop: 'clamp(6px, 2vw, 8px)', fontSize: 'clamp(13px, 3.5vw, 15px)' }}
                >
                  <IonIcon slot="start" icon={refreshOutline} />
                  YA FIRMÉ, VOLVER A VERIFICAR
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {/* mostrar mensaje si no hay recibos disponibles pero el acceso está habilitado */}
          {accesoHabilitado && salarios.length === 0 && (
            <IonCard>
              <IonCardContent>
                <p className="ion-text-center">No hay recibos salariales disponibles</p>
              </IonCardContent>
            </IonCard>
          )}

          {/* mostrar los recibos salariales si el acceso está habilitado y hay datos */}
          {accesoHabilitado && salarios.map((salario) => (
            <Recibo salario={salario} key={salario.mes} />
          ))}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default RecibosSalariales;
